import { toast } from 'react-toastify';
import {
    CreateProductBody,
    Product,
    ProductFilterParams,
    ProductResponse,
    UpdateProductBody,
} from '@core/models/product.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ProductService from 'services/api/product.service';

// -------------------------------------------------------------------------

interface IProductState {
    loading: boolean;
    inventoryLoading: boolean;
    productLifeCycleLoading: boolean;
    updateLoading: boolean;
    error: IErrorModel | undefined;
    products: Product[];
    product: Product | null;
    pagination: {
        pageNumber: number;
        pageSize: number;
        totalPages: number;
        totalElements: number;
    };
    inventoryHistory: [];
    productLifeCycle: [];
    productLoading: boolean;
}

const initialState: IProductState = {
    loading: false,
    inventoryLoading: false,
    productLifeCycleLoading: false,
    updateLoading: false,
    error: {} as IErrorModel,
    products: [],
    product: null,
    pagination: {
        pageNumber: 0,
        pageSize: 10,
        totalPages: 0,
        totalElements: 0,
    },
    inventoryHistory: [],
    productLifeCycle: [],
    productLoading: false,
};

// -------------------------------------------------------------------------

export const getProductsThunk = createAsyncThunk<
    ProductResponse,
    ProductFilterParams | undefined,
    { rejectValue: IErrorModel }
>(`/product/getProducts`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.getProducts(params);
        return response.data.products
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch products', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const getProductByIdThunk = createAsyncThunk<Product, number, { rejectValue: IErrorModel }>(
    `/product/getProductById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await ProductService.getProductById(id);
            return response.data.product
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch product', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createProductThunk = createAsyncThunk<
    ISuccessResponse,
    CreateProductBody,
    { rejectValue: IErrorModel }
>(`/product/createProduct`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.createProduct(body);
        toast.success(response.data.message || 'Product created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create product', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const updateProductThunk = createAsyncThunk<
    ISuccessResponse,
    UpdateProductBody,
    { rejectValue: IErrorModel }
>(`/product/updateProduct`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.updateProduct(body);
        toast.success(response.data.message || 'Product updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update product', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteProductThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/product/deleteProduct`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await ProductService.deleteProduct(id);
            toast.success(response.data.message || 'Product deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete product', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const exportProductsThunk = createAsyncThunk<
    any,
    { subCategories?: string; category?: string; dosages?: string; inStockOnly?: boolean },
    { rejectValue: IErrorModel }
>(`/product/exportProducts`, async ({ subCategories, category, dosages, inStockOnly }, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.exportProducts({ subCategories, category, dosages, inStockOnly });
        return response.data;
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});

export const getInventoryHistorybyProductThunk = createAsyncThunk<
    any,
    number,
    { rejectValue: IErrorModel }
>(`/product/getInventoryHistorybyProduct`, async (productId, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.getInventoryHistorybyProduct(productId);
        return response.data.transactions
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});

export const getProductLifeCycleThunk = createAsyncThunk<
    any,
    number,
    { rejectValue: IErrorModel }
>(`/product/getProductLifeCycle`, async (productId, { rejectWithValue }: any) => {
    try {
        const response: any = await ProductService.getProductLifeCycle(productId);
        return response.data.events
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});

// -------------------------------------------------------------------------

const productSlice = createSlice({
    name: `product`,
    initialState,
    reducers: {
        clearProduct(state) {
            state.product = null;
        },
    },
    extraReducers: (builder) => {
        // Get Products
        builder.addCase(getProductsThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getProductsThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.products = action.payload
            // if (action.payload.pageable) {
            //     state.pagination = {
            //         pageNumber: action.payload.pageable.pageNumber,
            //         pageSize: action.payload.pageable.pageSize,
            //         totalPages: action.payload.totalPages,
            //         totalElements: action.payload.totalElements,
            //     };
            // }
        });
        builder.addCase(getProductsThunk.rejected, (state, action) => {
            state.loading = false;
            state.products = [];
            state.error = action.payload;
        });

        // Get Product By Id
        builder.addCase(getProductByIdThunk.pending, (state) => {
            state.productLoading = true;
        });
        builder.addCase(getProductByIdThunk.fulfilled, (state, action) => {
            state.productLoading = false;
            state.product = action.payload;
        });
        builder.addCase(getProductByIdThunk.rejected, (state, action) => {
            state.productLoading = false;
            state.error = action.payload;
        });

        // Create Product
        builder.addCase(createProductThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createProductThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(createProductThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Update Product
        builder.addCase(updateProductThunk.pending, (state) => {
            state.updateLoading = true;
        });
        builder.addCase(updateProductThunk.fulfilled, (state) => {
            state.updateLoading = false;
        });
        builder.addCase(updateProductThunk.rejected, (state, action) => {
            state.updateLoading = false;
            state.error = action.payload;
        });

        // Delete Product
        builder.addCase(deleteProductThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteProductThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteProductThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Get Inventory History by Product
        builder.addCase(getInventoryHistorybyProductThunk.pending, (state) => {
            state.inventoryLoading = true;
        });
        builder.addCase(getInventoryHistorybyProductThunk.fulfilled, (state, action) => {
            state.inventoryLoading = false;
            state.inventoryHistory = action.payload;
        });
        builder.addCase(getInventoryHistorybyProductThunk.rejected, (state, action) => {
            state.inventoryLoading = false;
            state.error = action.payload;
        });

        // Get Product Life Cycle
        builder.addCase(getProductLifeCycleThunk.pending, (state) => {
            state.productLifeCycleLoading = true;
        });
        builder.addCase(getProductLifeCycleThunk.fulfilled, (state, action) => {
            state.productLifeCycleLoading = false;
            state.productLifeCycle = action.payload;
        });
        builder.addCase(getProductLifeCycleThunk.rejected, (state, action) => {
            state.productLifeCycleLoading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default productSlice;
export const { clearProduct } = productSlice.actions;
export const productReducer = productSlice.reducer;

